/*
 * api.js
 *
 * All API calls to CRBS are defined here.
 */

import { del, get, post } from "./http-utils";
import { getApiPath } from "./utils";

const API_ROOT = process.env.API_ROOT; // eslint-disable-line prefer-destructuring

let API_BASE_URL;
let API_URL;
if (process.env.NODE_ENV !== "production") {
  if (!process.env.API_URL || !API_ROOT) {
    console.warn(
      "Both API_URL and API_ROOT must be defined in .env.dev in development."
    );
  }
  API_BASE_URL = process.env.API_URL;
  API_URL = `${process.env.API_URL}${API_ROOT}`;
} else {
  if (!API_ROOT) {
    console.warn("API_ROOT must be defined in .env.prod in production.");
  }
  API_BASE_URL = getApiPath(window.location.pathname);
  API_URL = `${API_BASE_URL}${API_ROOT}`;
}

const PER_PAGE = 100; // num results to return
const STATUS_URL = `${API_BASE_URL}/status`;
const WHOAMI_URL = `${API_URL}/user/whoami`;
const PRODUCTS_URL = `${API_URL}/product`;
const SERVICES_URL = `${API_URL}/service`;
const RESOURCES_URL = `${API_URL}/resource`;
const RESOURCE_TYPES_URL = `${API_URL}/resource-type`;
const ROLES_URL = `${API_URL}/role`;
const USERS_URL = `${API_URL}/user`;
const GROUPS_URL = `${API_URL}/group`;
const POLICIES_URL = `${API_URL}/policy`;
const PERMISSIONS_URL = `${API_URL}/permission`;
const AUDIT_LOGS_URL = `${API_URL}/audit-log`;
const AUDIT_LOG_ENTITIES_URL = `${API_URL}/audit-log-entity`;
const CHANGESETS_URL = `${API_URL}/changeset`;

const api = {};

api.Status = {
  get: () =>
    fetch(STATUS_URL).then(response => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    })
};

api.User = {
  get: () => get(WHOAMI_URL)
};

api.Products = {
  get: page => get(PRODUCTS_URL, { per_page: PER_PAGE, page })
};

api.Services = {
  get: page => get(SERVICES_URL, { per_page: PER_PAGE, page }),
  create: service => post(SERVICES_URL, service),
  delete: service => del(`${API_BASE_URL}${service.$uri}`),
  register: (service, key) =>
    post(`${API_BASE_URL}${service.$uri}/credentials`, { [`public-key`]: key })
};

api.Resources = {
  get: page => get(RESOURCES_URL, { per_page: PER_PAGE, page })
};

api.ResourceTypes = {
  get: page => get(RESOURCE_TYPES_URL, { per_page: PER_PAGE, page })
};

api.Roles = {
  get: page => get(ROLES_URL, { per_page: PER_PAGE, page }),
  getByLabel: label => get(ROLES_URL, {}, { label })
};

api.Users = {
  get: page => get(USERS_URL, { per_page: PER_PAGE, page }),
  getByUsername: username => get(USERS_URL, {}, { username }),
  create: username => post(USERS_URL, { username }),
  delete: user => del(`${API_BASE_URL}${user.$uri}`)
};

api.Groups = {
  get: page => get(GROUPS_URL, { per_page: PER_PAGE, page }),
  getByName: name => get(GROUPS_URL, {}, { name }),
  create: name => post(GROUPS_URL, { name }),
  delete: group => del(`${API_BASE_URL}${group.$uri}`)
};

api.Policies = {
  get: page => get(POLICIES_URL, { per_page: PER_PAGE, page }),
  create: (policy, headers) => post(POLICIES_URL, policy, headers),
  delete: policy => del(`${API_BASE_URL}${policy.$uri}`)
};

api.Permissions = {
  get: page => get(PERMISSIONS_URL, { per_page: PER_PAGE, page })
};

api.AuditLogs = {
  get: page => get(AUDIT_LOGS_URL, { per_page: PER_PAGE, page })
};

api.AuditLogEntities = {
  get: page => get(AUDIT_LOG_ENTITIES_URL, { per_page: PER_PAGE, page })
};

api.Changesets = {
  get: page => get(CHANGESETS_URL, { per_page: PER_PAGE, page }),
  create: (message, getHeaders) =>
    post(CHANGESETS_URL, message, null, getHeaders)
};

export default api;
